import { useState } from "react";
import { X, Warning, MapPin, ArrowRight } from "@phosphor-icons/react";

/**
 * Modal shown right before a batch upload lands on an inspection job.
 * User picks a room → photos get tagged. User skips → warning + upload proceeds untagged
 * (the Additional Photos fallback still catches them, but the report reads cleanly if tagged).
 *
 * Props:
 *   open: boolean
 *   rooms: string[]
 *   count: number   — how many photos are queued
 *   kind: "before" | "after"   — for label text
 *   onCancel: () => void
 *   onPick: (room: string | null) => void  — null = skip untagged
 */
export default function RoomTagPickerModal({ open, rooms, count, kind, onCancel, onPick }) {
  const [selected, setSelected] = useState("");
  if (!open) return null;
  const phaseLabel = kind === "before" ? "Move-Out" : kind === "after" ? "Move-In" : "Inspection";

  return (
    <div
      className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="room-tag-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white w-full max-w-md border-2 border-[#0f172a] shadow-2xl">
        {/* Header */}
        <div className="bg-[#0f172a] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin weight="fill" size={20} className="text-amber-400" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Tag a Room</div>
          </div>
          <button onClick={onCancel} data-testid="room-tag-close" className="p-1 hover:bg-white/10">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="text-sm text-slate-700 leading-snug">
            You're about to upload <b>{count} photo{count === 1 ? "" : "s"}</b> to the <b>{phaseLabel}</b> inspection.
            Pick the room these photos belong to so they show up per-room in the finished report.
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto" data-testid="room-tag-options">
            {(rooms || []).map((r) => (
              <button
                key={r}
                onClick={() => setSelected(r)}
                data-testid={`room-tag-option-${r.replace(/\s+/g, "-").toLowerCase()}`}
                className={`h-11 px-3 text-xs font-black uppercase tracking-widest border-2 text-left transition ${
                  selected === r
                    ? "bg-[#0f172a] text-white border-[#0f172a]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"
                }`}
              >
                {r}
              </button>
            ))}
          </div>

          {/* Skip warning */}
          <div className="mt-5 border-2 border-amber-400 bg-amber-50 p-3 flex gap-2">
            <Warning weight="fill" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] leading-snug text-amber-900">
              <b>Skipping?</b> Photos still upload, but they'll land under
              "Additional Photos (Not Tagged)" in the report and email — not with the right room.
              Tag whenever possible.
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t-2 border-slate-200 px-4 py-3 flex items-center justify-between gap-2">
          <button
            onClick={() => onPick(null)}
            data-testid="room-tag-skip"
            className="h-10 px-4 text-[11px] font-black uppercase tracking-widest text-amber-800 hover:bg-amber-50 border-2 border-amber-400"
          >
            Skip · Upload Untagged
          </button>
          <button
            onClick={() => onPick(selected)}
            disabled={!selected}
            data-testid="room-tag-confirm"
            className="h-10 px-4 text-[11px] font-black uppercase tracking-widest bg-[#0f172a] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            Upload to {selected || "…"}
            <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
